import { Box } from '@mui/material';
import OverlayLinearProgress from '../../frame/components/progress/OverlayLinearProgress';
import AppContext, {
    isLocalTrack,
    OBJECT_TYPE_NAVIGATION_ALONE,
    OBJECT_TYPE_WEATHER,
    isTrack,
    isCloudTrack,
    isTravelTrack,
    isTrackAnalyzer,
    OBJECT_TYPE_STOP,
    OBJECT_TYPE_TRAVEL,
    TRAVEL_ROUTE_ID_PARAM,
    FAVORITES_URL_PARAM_FOLDER,
} from '../../context/AppContext';
import TravelContext from '../../context/TravelContext';
import React, { useState, useContext, useEffect, useCallback } from 'react';
import TrackTabList, { TRACK_TAB_IDS } from './tabs/TrackTabList';
import TrackContextMenu from './track/TrackContextMenu';
import isEmpty from 'lodash-es/isEmpty';
import { hasSegmentTurns, findGroupByName } from '../../manager/track/TracksManager';
import {
    FAVORITES_URL,
    FAVOURITES,
    INFO_MENU_URL,
    MAIN_URL_WITH_SLASH,
    MENU_INFO_CLOSE_SIZE,
    MENU_INFO_OPEN_SIZE,
    SHARE_FILE_MAIN_URL,
    SHARE_MENU_URL,
    TRACKS_URL,
    liveHash,
} from '../../manager/GlobalManager';
import { isTrackFromSearch, navigateBackToSearchResults } from '../../manager/SearchManager';
import { isVisibleTrack } from '../../menu/visibletracks/VisibleTracks';
import WptDetails from './wpt/WptDetails';
import WptPhotoList from './wpt/WptPhotoList';
import ShareFileMenu from '../../menu/share/ShareFileMenu';
import ShareFile from '../../menu/share/ShareFile';
import WptEditPanel from './favorite/WptEditPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUrlParams, encodeString } from '../../util/Utils';
import LoginContext from '../../context/LoginContext';
import MapContext from '../../context/MapContext';
import { useUpdateQueryParam } from '../../util/hooks/menu/useUpdateQueryParam';
import { useZoomToFit } from '../../util/hooks/map/useZoomToFit';
import { useFocusMode } from '../../util/hooks/map/useFocusMode';
import Loading from '../../menu/errors/Loading';

export default function InformationBlock({
    showInfoBlock,
    setShowInfoBlock,
    setClearState,
    mainMenuSize,
    setSavePrevState,
    trackUrlOpenLoading = false,
}) {
    const DRAWER_SIZE = 360;

    const ctx = useContext(AppContext);
    const ttx = useContext(TravelContext);
    const ltx = useContext(LoginContext);
    const mtx = useContext(MapContext);

    const navigate = useNavigate();
    const location = useLocation();
    const { updateQueryParam } = useUpdateQueryParam();
    const { restoreMapView } = useZoomToFit();
    const { clearSelectionFocus } = useFocusMode();

    const [tabsObj, setTabsObj] = useState(null);
    const [prevTrack, setPrevTrack] = useState(null);
    const [openWptDetails, setOpenWptDetails] = useState(false);
    const [openWptTab, setOpenWptTab] = useState(false);
    const [openShareFileMenu, setOpenShareFileMenu] = useState(false);
    const [openShareFileItem, setOpenShareFileItem] = useState(false);
    const [trackName, setTrackName] = useState(null);
    const [trackType, setTrackType] = useState(null);
    const [closeShareMenu, setCloseShareMenu] = useState(false);

    const showWptEditPanel = !!ctx.addFavorite?.location || !!ctx.addFavorite?.editWpt;
    const showTrackContextMenu = ctx.selectedGpxFile && (isTrack(ctx) || isTrackAnalyzer(ctx)) && !openShareFileItem;
    // first load of a travel route (get-osm-route): track not ready yet, show the loading page
    const showTravelLoading = ttx.processingTravelRouteByUrl && !showTrackContextMenu;

    /**
     * Handle Escape key to close PointContextMenu.
     * Located here (parent) to run Effect on closed menu.
     * Otherwise (if located in child) we can't catch removeEventListener moment.
     */
    const escapePointMenu = useCallback((e) => {
        if (e.key === 'Escape') {
            ctx.setPointContextMenu({});
        }
    }, []);
    useEffect(() => {
        window.removeEventListener('keydown', escapePointMenu);
        if (!isEmpty(ctx.pointContextMenu)) {
            window.addEventListener('keydown', escapePointMenu);
        }
    }, [ctx.pointContextMenu]);

    useEffect(() => {
        if (showWptEditPanel) {
            setShowInfoBlock(true);
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
        }
    }, [showWptEditPanel]);

    // Close WptEditPanel when the user navigates to another object or switches context
    useEffect(() => {
        const close = () =>
            ctx.setAddFavorite((prev) => {
                if (!prev?.location && !prev?.editWpt) return prev;
                return {
                    ...prev,
                    add: false,
                    location: null,
                    editTrack: false,
                    editWpt: null,
                    previewAppearance: null,
                };
            });
        if (ctx.exitGuards.wptEdit?.hasChanges) return;
        close();
    }, [ctx.selectedWpt, ctx.currentObjectType]);

    useEffect(() => {
        if (closeShareMenu) {
            setCloseShareMenu(false);
            if (showTrackContextMenu && trackName) {
                navigate(
                    {
                        pathname:
                            MAIN_URL_WITH_SLASH +
                            TRACKS_URL +
                            INFO_MENU_URL +
                            encodeURIComponent(encodeString(trackName)),
                        hash: window.location.hash,
                    },
                    { replace: true }
                );
            } else {
                setTrackName(null);
                navigate(MAIN_URL_WITH_SLASH + trackType);
            }
        }
    }, [closeShareMenu]);

    // close share file item when track is closed
    useEffect(() => {
        if (ctx.selectedGpxFile && isEmpty(ctx.selectedGpxFile) && openShareFileItem) {
            setOpenShareFileItem(false);
        }
    }, [ctx.selectedGpxFile]);

    useEffect(() => {
        if (!showInfoBlock) {
            // stop-editor (close button)
            stopCreatedTrack(false);
            ctx.mutateShowPoints({ points: true, wpts: true });
            ctx.setTrackRange(null);
            setClearState(true);
            if (!ctx.currentObjectType) {
                setTrackName(null);
                hideTrackFromMapIfNotVisible(ctx.selectedGpxFile);
                if (!isEmpty(ctx.selectedGpxFile)) {
                    ctx.setSelectedGpxFile({});
                }
            }
        }
    }, [showInfoBlock]);

    useEffect(() => {
        const width = getWidth();
        const px = parseFloat(width) || 0; // 100px -> 100, auto -> 0
        const padding = px || DRAWER_SIZE + Number(mainMenuSize.replace('px', '')) + 24; // always apply right padding on desktop
        mtx.mutateFitBoundsPadding((o) => (o.left = padding));
    }, [showInfoBlock]);

    useEffect(() => {
        if (location.pathname.includes(SHARE_FILE_MAIN_URL)) {
            setOpenShareFileItem(true);
        } else {
            setOpenShareFileItem(false);
        }
    }, [showInfoBlock]);

    // update URL for info track menu
    useEffect(() => {
        if (trackName && !ctx.shareFile) {
            navigate(
                {
                    pathname:
                        MAIN_URL_WITH_SLASH + TRACKS_URL + INFO_MENU_URL + encodeURIComponent(encodeString(trackName)),
                    hash: window.location.hash,
                },
                { replace: true }
            );
        }
    }, [trackName]);

    // a renamed track keeps its menu open, so the URL follows its new name
    useEffect(() => {
        const name = ctx.selectedGpxFile?.name;
        if (trackName && name && name !== trackName && !ctx.shareFile && isCloudTrack(ctx)) {
            setTrackName(name);
        }
    }, [ctx.selectedGpxFile?.name]);

    // open favorite info menu when marker is selected
    useEffect(() => {
        if (
            ctx.selectedGpxFile.file?.type === FAVOURITES &&
            !ctx.shareFile &&
            ctx.selectedGpxFile.markerCurrent &&
            ctx.selectedGpxFile.name
        ) {
            const favName = ctx.selectedGpxFile.name;
            const favGroup = ctx.selectedGpxFile.nameGroup;
            const fullName =
                encodeURIComponent(encodeString(favGroup)) + '/' + encodeURIComponent(encodeString(favName));
            const folder = ctx.selectedGpxFile.openedFolder;
            navigate(
                {
                    pathname: MAIN_URL_WITH_SLASH + FAVORITES_URL + INFO_MENU_URL + fullName,
                    search: folder ? createUrlParams({ [FAVORITES_URL_PARAM_FOLDER]: folder }) : undefined,
                    hash: globalThis.location.hash,
                },
                { replace: true }
            );
        }
    }, [ctx.selectedGpxFile.markerCurrent]);

    // detect leaving from Local Track Editor when another kind of object type is activated
    useEffect(() => {
        if (ctx.currentObjectType && isLocalTrack(ctx) === false && ctx.createTrack) {
            stopCreatedTrack(true);
        }
    }, [ctx.currentObjectType]);

    // used to add Turns tab when the turns appeared
    useEffect(() => {
        isLocalTrack(ctx) && ctx.setUpdateInfoBlock(true);
    }, [hasSegmentTurns({ track: ctx.selectedGpxFile })]);

    useEffect(() => {
        const keepOpen = ttx.processingTravelRouteByUrl;
        if (
            (!ctx.selectedGpxFile || isEmpty(ctx.selectedGpxFile)) &&
            ctx.currentObjectType !== OBJECT_TYPE_WEATHER &&
            !keepOpen
        ) {
            setPrevTrack(null);
            setTabsObj(null);
            setShowInfoBlock(false);
        } else {
            if (!ctx.currentObjectType && !keepOpen) {
                setTabsObj(null);
                setShowInfoBlock(false);
            } else if (ctx.updateInfoBlock || !prevTrack || Object.keys(prevTrack).length === 0 || !showInfoBlock) {
                let tObj;
                setPrevTrack(ctx.selectedGpxFile);
                ctx.setUpdateInfoBlock(false);
                if (ctx.currentObjectType === OBJECT_TYPE_NAVIGATION_ALONE) {
                    // don't display InfoBlock in Navigation menu until details requested
                } else if (showTrackContextMenu) {
                    // finally assume that default selectedGpxFile is a track
                    tObj = new TrackTabList().create(ctx, setShowInfoBlock);
                    if (isCloudTrack(ctx)) {
                        // set track identification for URL
                        setTrackName(ctx.selectedGpxFile.name);
                        setTrackType(TRACKS_URL);
                        setOpenWptDetails(false);
                    }
                }
                if (tObj) {
                    setShowInfoBlock(true);
                    clearStateIfObjChange();
                    setTabsObj(tObj);
                }
            }
        }
    }, [ctx.currentObjectType, ctx.selectedGpxFile, ctx.updateInfoBlock]);

    useEffect(() => {
        ctx.setPhotoGallery(null);
        if (ctx.selectedWpt) {
            setShowInfoBlock(true);
            setOpenWptDetails(true);
        } else {
            setOpenWptDetails(false);
        }
    }, [ctx.selectedWpt]);

    // share file menu
    useEffect(() => {
        if (ctx.shareFile?.mainFile.name) {
            setOpenShareFileMenu(true);
            if (!showInfoBlock) {
                setShowInfoBlock(true);
            }
            const name = ctx.shareFile.mainFile.name;
            const typeUrl = ctx.shareFile.mainFile.type === FAVOURITES ? FAVORITES_URL : TRACKS_URL;
            setTrackName(name);
            setTrackType(typeUrl);

            navigate(
                MAIN_URL_WITH_SLASH +
                    typeUrl +
                    INFO_MENU_URL +
                    encodeURIComponent(encodeString(name)) +
                    '/' +
                    SHARE_MENU_URL,
                {
                    replace: true,
                }
            );
        } else {
            setOpenShareFileMenu(false);
        }
    }, [ctx.shareFile]);

    useEffect(() => {
        if (!ltx.loginUser) {
            setShowInfoBlock(false);
            setOpenWptDetails(false);
        }
    }, [ltx.loginUser]);

    // Open TrackContextMenu after clicking a waypoint on the map.
    useEffect(() => {
        if (openWptTab) {
            const tObj = new TrackTabList().create(ctx, setShowInfoBlock);
            clearStateIfObjChange();
            tObj.defaultTab = TRACK_TAB_IDS.POINTS;
            setTabsObj(tObj);
            setOpenWptDetails(false);
            setOpenWptTab(false);
        }
    }, [openWptTab]);

    function hideTrackFromMapIfNotVisible(track) {
        if (!isEmpty(track) && !isVisibleTrack(track)) {
            if (!isEmpty(ctx.gpxFiles) && ctx.gpxFiles[track.name]) {
                ctx.mutateGpxFiles((o) => (o[track.name].url = null));
            }
            // remove share file from visible tracks
            if (ctx.shareWithMeFiles?.tracks[track.name]) {
                ctx.setShareWithMeFiles({
                    ...ctx.shareWithMeFiles,
                    tracks: {
                        ...ctx.shareWithMeFiles.tracks,
                        [track.name]: {
                            ...ctx.shareWithMeFiles.tracks[track.name],
                            url: null,
                        },
                    },
                });
            }
        }
    }

    function clearStateIfObjChange() {
        if (
            prevTrack &&
            prevTrack.name !== ctx.selectedGpxFile.name &&
            prevTrack.points?.length !== ctx.selectedGpxFile?.points?.length
        ) {
            setClearState(true);
        } else {
            setClearState(false);
        }
    }

    function stopCreatedTrack(deletePrev) {
        if (ctx.createTrack) {
            ctx.createTrack.enable = false; // stop-editor
            if (deletePrev) {
                ctx.createTrack.deletePrev = deletePrev;
            }
            ctx.setCreateTrack({ ...ctx.createTrack });
            ctx.setAddFavorite((prev) => ({ ...prev, editTrack: false }));
        }
    }

    function getWidth() {
        if (showInfoBlock) {
            return `${DRAWER_SIZE + 24}px`;
        } else {
            return `${MENU_INFO_CLOSE_SIZE}px`;
        }
    }

    function isOpenMainFavShareFile() {
        const isCloseFavItemDetails = !ctx.selectedGpxFile?.markerCurrent;
        return openShareFileItem && isCloseFavItemDetails;
    }

    function handleCloseTrackContextMenu() {
        setShowInfoBlock(false);

        if (!isTrackAnalyzer(ctx)) {
            closeTrackAnalyzer();
        }
        if (ctx.selectedGpxFile.mapObj) {
            closeMapObjectMenu();
        } else if (isCloudTrack(ctx)) {
            closeCloudTrack({ fromSearch: isTrackFromSearch(ctx) });
        } else if (isLocalTrack(ctx)) {
            if (!isEmpty(ctx.selectedGpxFile)) {
                ctx.setSelectedGpxFile({});
            }
            ctx.setSelectedLocalTrackObj(null);
        } else if (isTravelTrack(ctx)) {
            if (!isEmpty(ctx.selectedGpxFile)) {
                ctx.setSelectedGpxFile({});
            }
        }
        restoreMapView();
        clearSelectionFocus();
    }

    function closeMapObjectMenu() {
        ctx.setCloseMapObj(true);
        if (!isEmpty(ctx.gpxFiles) && ctx.gpxFiles[ctx.selectedGpxFile.name]) {
            ctx.mutateGpxFiles((o) => (o[ctx.selectedGpxFile.name].mapObj = null));
        }
    }

    function closeTrackAnalyzer() {
        // not change object type if track analyzer is active, because return to prev menu
        if (ctx.currentObjectType === OBJECT_TYPE_STOP) {
            ctx.setSelectedTransportRoute(null);
        }
        if (ctx.currentObjectType === OBJECT_TYPE_TRAVEL) {
            ttx.setSelectedTravelRoute(null);
            updateQueryParam({ key: TRAVEL_ROUTE_ID_PARAM, value: null });
        }
        ctx.setCurrentObjectType(null);
    }

    function closeCloudTrack({ fromSearch = false } = {}) {
        hideTrackFromMapIfNotVisible(ctx.selectedGpxFile);

        // If openGroups is empty (e.g. track was opened directly from Garmin last-sync menu),
        // rebuild the full folder stack from the track name so the back button navigates
        // correctly through each parent folder (openGroups acts as a stack — pop() goes up one level).
        if (!ctx.openGroups || ctx.openGroups.length === 0) {
            const fileName = ctx.selectedGpxFile?.name;
            if (fileName) {
                const folderParts = fileName.split('/').slice(0, -1);
                if (folderParts.length > 0) {
                    const groups = folderParts
                        .map((_, i) => findGroupByName(ctx.tracksGroups, folderParts.slice(0, i + 1).join('/')))
                        .filter(Boolean);
                    if (groups.length > 0) {
                        ctx.setOpenGroups(groups);
                    }
                }
            }
        }

        if (!isEmpty(ctx.selectedGpxFile)) {
            ctx.setSelectedGpxFile({});
        }
        setTrackName(null);
        setSavePrevState(true);
        ctx.setSelectedCloudTrackObj(null);

        if (fromSearch) {
            ctx.setSelectedSearchObj(null);
            if (navigateBackToSearchResults(navigate, ctx, location)) {
                return;
            }
        }

        navigate({
            pathname: MAIN_URL_WITH_SLASH + trackType,
            hash: liveHash(),
        });
    }

    return (
        <>
            {(trackUrlOpenLoading || showTravelLoading) && (
                <Box
                    sx={{
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Loading />
                </Box>
            )}
            {showInfoBlock &&
                !trackUrlOpenLoading &&
                !showTravelLoading &&
                (showWptEditPanel ? (
                    <WptEditPanel key={ctx.addFavorite?.openKey} setShowInfoBlock={setShowInfoBlock} />
                ) : openWptDetails ? (
                    ctx.photoGallery ? (
                        <WptPhotoList photos={ctx.photoGallery} />
                    ) : (
                        <WptDetails setOpenWptTab={setOpenWptTab} setShowInfoBlock={setShowInfoBlock} />
                    )
                ) : openShareFileMenu ? (
                    <ShareFileMenu
                        setShowInfoBlock={setShowInfoBlock}
                        setCloseShareMenu={setCloseShareMenu}
                        returnToTrack={showTrackContextMenu}
                    />
                ) : isOpenMainFavShareFile() ? (
                    <ShareFile />
                ) : (
                    <>
                        {(ctx.loadingContextMenu || ctx.gpxLoading || ttx.processingTravelRouteByUrl) && (
                            <OverlayLinearProgress />
                        )}
                        {ctx.updateFiles && <OverlayLinearProgress id="se-info-files-loading" />}
                        {showTrackContextMenu && (
                            <TrackContextMenu
                                track={ctx.selectedGpxFile}
                                onClose={handleCloseTrackContextMenu}
                                tabsObj={tabsObj}
                                showBackButton={!ctx.selectedGpxFile?.mapObj}
                            />
                        )}
                    </>
                ))}
        </>
    );
}
